import { apiSlice } from './api-slice';
import {
  Response,
  Sortable,
  FormFeature,
  Form,
  FormTemplate,
  UpdatedFormTemplate,
} from '../models';
import { PAGINATION_SIZE as paginatorSize } from '../constants';

export const formsApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getAllFormFeatures: builder.query<Response<FormFeature[]>, void>({
      query: () => ({
        url: `company/formBuilding/formFeatureTypes`,
        method: 'GET',
      }),
    }),
    getFormTemplateById: builder.query<Response<Form>, number>({
      query: formTemplateId => ({
        url: `company/formBuilding/formTemplate/${formTemplateId}`,
        method: 'GET',
      }),
      providesTags: (_, __, id) => [{ type: 'Form', id }],
    }),
    getAllFormTemplates: builder.query<
      Response<Sortable<FormTemplate>>,
      { size?: number; page: number }
    >({
      query: ({ page, size }) => ({
        url: `company/formBuilding/formTemplates`,
        method: 'GET',
        params: {
          size: size || paginatorSize,
          page,
          name: 't',
        },
      }),
      providesTags: result =>
        result?.data.content.length
          ? result.data.content.map(({ id }) => ({ type: 'Form', id }))
          : ['Form'],
    }),
    getAllActiveForms: builder.query<Response<Form[]>, { phaseIds?: string }>({
      query: ({ phaseIds }) => ({
        url: `company/formBuilding/allActiveForms`,
        method: 'GET',
        params: {
          phaseIds,
        },
      }),
    }),
    getUserPreference: builder.query<
      {
        id?: number;
        formTemplateId?: number;
        tempFormId?: number;
        field: string;
      }[],
      any
    >({
      query: () => ({
        url: `company/userPreference`,
        method: 'GET',
      }),
    }),
    changeUserPreference: builder.mutation<
      { id?: number; formTemplateId: number; field: string }[],
      { preferences: { formTemplateId: number; field: string }[] }
    >({
      query: data => ({
        url: 'company/userPreference',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Form' }],
    }),
    createFormTemplate: builder.mutation<Response<Form>, Form>({
      query: data => ({
        url: 'company/formBuilding/formTemplate',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'Form' }],
    }),
    updateFormTemplate: builder.mutation<Response<Form>, UpdatedFormTemplate>({
      query: data => ({
        url: 'company/formBuilding/formTemplate',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: () => {
        return [{ type: 'Form' }];
      },
    }),
    deleteFormTemplate: builder.mutation<Response<null>, { id: number }>({
      query: ({ id }) => ({
        url: `company/formBuilding/formTemplate/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: () => {
        return [{ type: 'Form' }];
      },
    }),
  }),
});

export const {
  useGetAllFormFeaturesQuery,
  useGetAllActiveFormsQuery,
  useGetFormTemplateByIdQuery,
  useGetAllFormTemplatesQuery,
  useCreateFormTemplateMutation,
  useUpdateFormTemplateMutation,
  useDeleteFormTemplateMutation,
  useGetUserPreferenceQuery,
  useChangeUserPreferenceMutation,
} = formsApiSlice;
